import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Box, Button, Chip, Grid, Paper, Typography } from "@material-ui/core";
import { ArrowBack, AssignmentTurnedInOutlined, CheckCircleOutline, ConfirmationNumberOutlined, DateRangeOutlined, HourglassEmpty, Refresh, TimerOffOutlined, VpnKeyOutlined } from "@material-ui/icons";
import { Alert, Skeleton } from "@material-ui/lab";
import PageContainer from "@jumbo/components/PageComponents/layouts/PageContainer";
import AnswerReview from "@jumbo/Dashboards/FormBuilder/FillFormDetails/AnswerReview";
import DetailRow from "@jumbo/Dashboards/FormBuilder/FillFormDetails/DetailRow";
import ParticipantDetails from "@jumbo/Dashboards/FormBuilder/FillFormDetails/ParticipantDetails";
import useStyles from "@jumbo/Dashboards/FormBuilder/FillFormDetails/detailStyles";
import useListStyles from "@jumbo/Dashboards/FormBuilder/recordListStyles";
import axios from "services/auth/jwt/config";
import { statuses, errorText, dateText } from "./utils";

const statusIcons = { inProgress: <HourglassEmpty />, submitted: <CheckCircleOutline />, expired: <TimerOffOutlined /> };

function TicketAnswers({ id, ticketId }) {
  const classes = useStyles();
  const list = useListStyles();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    axios.get(`/formticket/admin/${id}/${ticketId}`)
      .then(({ data }) => {
        if (!data?.ticket || data.ticket._id !== ticketId || !data.form || (data.form._id && data.form._id !== id)) throw new Error("بلیط آزمون یافت نشد");
        if (active) setResult(data);
      })
      .catch(err => { if (active) setError(errorText(err)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [id, ticketId, revision]);
  const ticket = result?.ticket;
  const form = result?.form;
  const correct = ticket?.correctAnswerCount, total = ticket?.scoredQuestionCount;
  const score = Number.isFinite(correct) && Number.isFinite(total) && correct >= 0 && total >= correct ? { correct, total } : null;
  return <PageContainer heading="پاسخ‌های ذخیره‌شده آزمون" breadcrumbs={[
    { label: "فرم‌ها", link: "/forms" },
    { label: "بلیط‌های آزمون", link: `/formtickets/${id}` },
    { label: "نمایش پاسخ", isActive: true },
  ]}>
    <Box className={list.hero}>
      <Box className={list.heroIdentity}>
        <Box className={list.heroIcon}><ConfirmationNumberOutlined /></Box>
        <Box><Typography className={list.eyebrow}>مرور پاسخ‌های ذخیره‌شده آزمون</Typography>
          <Typography component="h2" className={list.heroTitle}>{form?.title || "پاسخ‌های بلیط آزمون"}</Typography></Box>
      </Box>
      <Box className={list.heroActions}>
        <Button component={Link} to={`/formtickets/${id}`} startIcon={<ArrowBack />} className={list.button}>بازگشت به بلیط‌ها</Button>
        <Button component={Link} to={`/forminputs/${id}?name=${encodeURIComponent(form?.title || "")}`} color="primary" variant="outlined" startIcon={<AssignmentTurnedInOutlined />} className={list.button}>ورودی‌های فرم</Button>
        <Button color="primary" disabled={loading} startIcon={<Refresh />} className={list.button} onClick={() => setRevision(value => value + 1)}>بارگذاری مجدد</Button>
      </Box>
    </Box>
      {loading ? <Paper elevation={0} className={`${classes.panel} ${classes.loading}`}>
        <Typography role="status" color="textSecondary">در حال دریافت پاسخ‌ها…</Typography>
        {[1, 2, 3].map(value => <Skeleton key={value} variant="rect" height={90} aria-hidden="true" />)}
      </Paper> : error ? <Alert severity="error">{error}</Alert> : ticket && <>
        {ticket.status !== "submitted" && <Box mb={3}><Alert severity={ticket.status === "expired" ? "warning" : "info"}>
          {ticket.status === "expired" ? "مهلت پاسخگویی این بلیط پایان یافته است؛ آزمون هنوز نهایی نشده است." : "این آزمون هنوز نهایی نشده است."}
        </Alert></Box>}
        <Grid container spacing={3} className={classes.layout}>
          <Grid item md={8} xs={12} className={classes.column}>
            <AnswerReview fields={ticket.questions} score={score} search={search} filter={filter} onSearchChange={setSearch} onFilterChange={setFilter}
              summaryTitle="نتیجه پاسخ‌های ذخیره‌شده" summaryDescription="این خلاصه بر اساس پاسخ‌های ذخیره‌شده تا آخرین بارگذاری است." answerTitle="پاسخ‌های ذخیره‌شده"
              emptyText="سوالی برای این بلیط ثبت نشده است." />
          </Grid>
          <Grid item md={4} xs={12} className={classes.column}>
            <ParticipantDetails owner={ticket.owner} privacyMode={!form || Boolean(form.privacyMode)} />
            <Paper component="section" aria-label="جزئیات بلیط آزمون" elevation={0} className={classes.panel}>
              <Box className={classes.panelHeading}><Typography component="h3" className={classes.heading}><ConfirmationNumberOutlined />جزئیات بلیط آزمون</Typography></Box>
              <Box component="dl" className={classes.details}>
                <DetailRow label="شناسه بلیط" icon={<VpnKeyOutlined />}><Box component="code" className={classes.identifier}>{ticket._id}</Box></DetailRow>
                <DetailRow label="وضعیت" icon={statusIcons[ticket.status]}><Chip size="small" label={statuses[ticket.status]} icon={statusIcons[ticket.status]} className={`${list.status} ${list[ticket.status] || ""}`} /></DetailRow>
                <DetailRow label="پاسخ‌های ذخیره‌شده" icon={<AssignmentTurnedInOutlined />}>{(ticket.answerCount || 0).toLocaleString("fa-IR")} از {(ticket.questionCount || 0).toLocaleString("fa-IR")} سوال</DetailRow>
                <DetailRow label="شروع آزمون" icon={<DateRangeOutlined />}>{dateText(ticket.createdAt)}</DetailRow>
                <DetailRow label="مهلت پاسخگویی" icon={<DateRangeOutlined />}>{ticket.durationEnabled ? dateText(ticket.expiresAt) : "بدون محدودیت زمانی"}</DetailRow>
                <DetailRow label="آخرین به‌روزرسانی" icon={<DateRangeOutlined />}>{dateText(ticket.updatedAt)}</DetailRow>
                {result.serverNow && <DetailRow label="آخرین بارگذاری" icon={<Refresh />}>{dateText(result.serverNow)}</DetailRow>}
              </Box>
              {ticket.formFilled && <Box className={classes.consumeAction}>
                <Button component={Link} fullWidth variant="contained" to={`/formFillDetails/${ticket.formFilled}`} color="primary" startIcon={<AssignmentTurnedInOutlined />} className={list.button}>نمایش پاسخ نهایی</Button>
              </Box>}
            </Paper>
          </Grid>
        </Grid>
      </>}
  </PageContainer>;
}

export default function Answers() {
  const { id, ticketId } = useParams();
  return <TicketAnswers key={`${id}:${ticketId}`} id={id} ticketId={ticketId} />;
}
